import React, { useState, useEffect } from "react";
import Navbar from "../Components/Navbar";
import HelpMessage from "../Components/HelpMessage";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";

const FieldNotes = () => {
  const navigate = useNavigate();
  const currentUser = localStorage.getItem("currentUser");
  const { monthYear } = useParams();

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("create");
  const [selectedNote, setSelectedNote] = useState(null);
  const [composerText, setComposerText] = useState("");
  const [detailDraft, setDetailDraft] = useState("");

  const formattedMonthLabel = monthYear
    ? moment(monthYear, "M-YYYY").format("MMMM YYYY")
    : "";

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASEURL}/api/field-notes?supplier=${currentUser}&monthYear=${monthYear}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const fetchedNotes = response.data.notes || [];
      setNotes(fetchedNotes);

      if (fetchedNotes.length > 0) {
        const first = fetchedNotes[0];
        setSelectedNote(first);
        setComposerText("");
        setDetailDraft("");
        setMode("view");
      } else {
        setSelectedNote(null);
        setComposerText("");
        setMode("create");
        setDetailDraft("");
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
      toast.error("Unable to load notes");
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, monthYear]);

  const handleComposerChange = (event) => {
    setComposerText(event.target.value);
  };

  const saveNote = async (payload, successMessage) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASEURL}/api/field-notes`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success(successMessage);
      setComposerText("");
      fetchNotes();
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error("Unable to save note");
    }
  };

  const handleSendToSelf = () => {
    if (!composerText.trim()) {
      toast.info("Add a message before saving");
      return;
    }
    saveNote(
      {
        note: composerText,
        supplier: currentUser,
        note_type: "self",
        monthYear,
      },
      "Note saved to your timeline"
    );
  };

  const handleSendToAurivo = () => {
    if (!composerText.trim()) {
      toast.info("Add a message before sending");
      return;
    }
    saveNote(
      {
        note: composerText,
        supplier: currentUser,
        note_type: "team",
        note_to: "Aurivo",
        monthYear,
      },
      "Sent to your advisor"
    );
  };

  const handleSelectNote = (note) => {
    setSelectedNote(note);
    setMode("view");
    setComposerText("");
    setDetailDraft("");
  };

  const handleEditNote = (note, event) => {
    if (event) {
      event.stopPropagation();
    }
    setSelectedNote(note);
    setComposerText("");
    setDetailDraft(note.note || "");
    setMode("edit");
  };

  const handleDeleteNote = async (note, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (!window.confirm("Delete this note?")) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASEURL}/api/field-notes/${note.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          // Some APIs require the current supplier context for deletes
          params: {
            supplier: currentUser,
          },
        }
      );
      toast.success("Note deleted");
      if (selectedNote?.id === note.id) {
        setSelectedNote(null);
        setMode("create");
        setComposerText("");
        setDetailDraft("");
      }
      fetchNotes();
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error("Unable to delete note");
    }
  };

  const handleSaveEdit = async () => {
    if (!detailDraft.trim() || !selectedNote) {
      toast.info("Update the note before saving");
      return;
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASEURL}/api/field-notes/${selectedNote.id}`,
        { note: detailDraft },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Note updated");
      setComposerText("");
      setDetailDraft("");
      setMode("view");
      fetchNotes();
    } catch (error) {
      console.error("Error updating note:", error);
      toast.error("Unable to update note");
    }
  };

  const isEditing = mode === "edit";
  const detailText = selectedNote ? selectedNote.note : "Select a note to preview it here.";
  const detailMeta = selectedNote
    ? `${moment(selectedNote.created_at).format("DD MMM YYYY")} • ${selectedNote.note_to ? selectedNote.note_to : "Me"}`
    : "";
  const isDetailEditable = mode === "edit" && !!selectedNote;
  const detailValue = isDetailEditable ? detailDraft : selectedNote?.note || "";

  return (
    <div className="customer-layout">
      <Navbar />
      <main className="customer-main">
        <section className="notes-composer card">
          <header className="card-header">
            <button
              type="button"
              className="button ghost sm"
              onClick={() => navigate(-1)}
            >
              <i className="uicon-arrow-narrow-left" /> Back
            </button>
            <h1 className="card-title">Field Notes</h1>
            <div className="header-spacer" aria-hidden="true" />
          </header>
          <textarea
            placeholder={isEditing ? "Editing note in detail section below…" : "Type your message here"}
            value={isEditing ? "" : composerText}
            onChange={handleComposerChange}
            rows={5}
            disabled={isEditing}
          />
          {isEditing ? (
            <div className="notes-composer__info">Editing selected note below.</div>
          ) : (
            <div className="notes-composer__actions">
              <button className="button ghost" onClick={handleSendToAurivo}>
                Send to Aurivo
              </button>
              <button className="button" onClick={handleSendToSelf}>
                Save
              </button>
            </div>
          )}
        </section>

        <section className="notes-session card">
          <header className="notes-session__header">
            <h2>Timeline</h2>
          </header>
          {loading ? (
            <div className="notes-empty">Loading notes…</div>
          ) : notes.length === 0 ? (
            <div className="notes-empty">No notes captured yet.</div>
          ) : (
            <div className="notes-timeline">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">From</th>
                    <th scope="col">Detail</th>
                    <th scope="col" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {notes.map((note) => (
                    <tr
                      key={note.id}
                      className={selectedNote?.id === note.id ? "selected" : ""}
                      onClick={() => handleSelectNote(note)}
                    >
                      <td>{moment(note.created_at).format("DD/MM/YYYY")}</td>
                      <td>{note.note_to ? note.note_to : "Me"}</td>
                      <td>{note.note}</td>
                      <td className="notes-row__actions">
                        <button
                          type="button"
                          className="icon-button"
                          onClick={(event) => handleEditNote(note, event)}
                          aria-label={`Edit note from ${moment(note.created_at).format("DD/MM/YYYY")}`}
                        >
                          <i className="uicon-edit" />
                        </button>
                        <button
                          type="button"
                          className="icon-button danger"
                          onClick={(event) => handleDeleteNote(note, event)}
                          aria-label={`Delete note from ${moment(note.created_at).format("DD/MM/YYYY")}`}
                        >
                          <i className="uicon-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="notes-detail card">
          <header>
            <h2>Detail</h2>
            {detailMeta && <span className="notes-detail__meta">{detailMeta}</span>}
          </header>
          <div className="notes-detail__body">
            {selectedNote ? (
              <textarea
                className="notes-detail__textarea"
                value={detailValue}
                onChange={(event) => {
                  if (mode === "edit") {
                    setDetailDraft(event.target.value);
                  }
                }}
                rows={6}
                disabled={!isDetailEditable}
                aria-label="Selected note detail"
              />
            ) : (
              <div className="notes-detail__placeholder">{detailText}</div>
            )}
          </div>
          {isDetailEditable && (
            <div className="notes-detail__actions">
              <button className="button ghost" onClick={() => {
                setMode("view");
                setDetailDraft("");
              }}>
                Cancel
              </button>
              <button className="button" onClick={handleSaveEdit}>
                Save Changes
              </button>
            </div>
          )}
        </section>

        <div className="home-help">
          <HelpMessage />
        </div>
      </main>
    </div>
  );
};

export default FieldNotes;
